/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * useDeviceFingerprint Hook
 * ============================================================================
 * 
 * Generates a device fingerprint for trusted device functionality.
 * Uses browser, OS, and screen resolution.
 * 
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react';

export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
}

export interface DeviceFingerprint {
  hash: string;
  info: DeviceInfo;
}

/**
 * Simple hash function for fingerprint generation
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convert to hex and pad
  const hex = Math.abs(hash).toString(16);
  return hex.padStart(8, '0') + '-' + 
         Date.now().toString(16).slice(-8) + '-' +
         Math.random().toString(16).slice(2, 10);
}

/**
 * Detect browser name and version
 */
function detectBrowser(): { name: string; version: string } {
  const ua = navigator.userAgent;
  
  // Order matters - check more specific browsers first
  const browsers = [
    { name: 'Edge', regex: /Edg(?:e|A|iOS)?\/(\d+)/ },
    { name: 'Opera', regex: /(?:Opera|OPR)\/(\d+)/ },
    { name: 'Chrome', regex: /Chrome\/(\d+)/ },
    { name: 'Safari', regex: /Version\/(\d+).*Safari/ },
    { name: 'Firefox', regex: /Firefox\/(\d+)/ },
    { name: 'IE', regex: /(?:MSIE |Trident.*rv:)(\d+)/ },
  ];

  for (const { name, regex } of browsers) {
    const match = ua.match(regex);
    if (match) {
      return { name, version: match[1] || 'unknown' };
    }
  }

  return { name: 'Unknown', version: 'unknown' };
}

/**
 * Detect OS name and version
 */
function detectOS(): { name: string; version: string } {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  const osPatterns = [
    { name: 'Windows 11', regex: /Windows NT 10\.0.*Win64/ },
    { name: 'Windows 10', regex: /Windows NT 10\.0/ },
    { name: 'Windows 8.1', regex: /Windows NT 6\.3/ },
    { name: 'Windows 8', regex: /Windows NT 6\.2/ },
    { name: 'Windows 7', regex: /Windows NT 6\.1/ },
    { name: 'macOS', regex: /Mac OS X (\d+[._]\d+)/ },
    { name: 'iOS', regex: /iPhone|iPad|iPod/ },
    { name: 'Android', regex: /Android (\d+\.?\d*)/ },
    { name: 'Linux', regex: /Linux/ },
    { name: 'Chrome OS', regex: /CrOS/ },
  ];

  for (const { name, regex } of osPatterns) {
    const match = ua.match(regex);
    if (match) {
      const version = match[1]?.replace('_', '.') || '';
      return { name, version };
    }
  }

  return { name: platform || 'Unknown', version: '' };
}

/**
 * Get screen resolution
 */
function getScreenResolution(): string {
  const width = window.screen.width;
  const height = window.screen.height;
  const pixelRatio = window.devicePixelRatio || 1;
  return `${width}x${height}@${pixelRatio}x`;
}

/**
 * Generate device info
 */
function generateDeviceInfo(): DeviceInfo {
  const browser = detectBrowser();
  const os = detectOS();

  return {
    browser: browser.name,
    browserVersion: browser.version,
    os: os.name,
    osVersion: os.version,
    screenResolution: getScreenResolution(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
  };
}

/**
 * Generate fingerprint hash from device info
 */
function generateFingerprint(info: DeviceInfo): string {
  const fingerprintData = [
    info.browser,
    info.browserVersion,
    info.os,
    info.osVersion,
    info.screenResolution,
    info.timezone,
    info.language,
    info.platform,
  ].join('|');

  return simpleHash(fingerprintData);
}

/**
 * Hook to get device fingerprint
 */
export function useDeviceFingerprint(): {
  fingerprint: DeviceFingerprint | null;
  isLoading: boolean;
  regenerate: () => void;
} {
  const [fingerprint, setFingerprint] = useState<DeviceFingerprint | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const generate = useCallback(() => {
    setIsLoading(true);
    
    // Small delay to ensure all browser APIs are available
    setTimeout(() => {
      try {
        const info = generateDeviceInfo();
        const hash = generateFingerprint(info);
        setFingerprint({ hash, info });
      } catch (error) {
        console.error('Failed to generate device fingerprint:', error);
        setFingerprint({
          hash: simpleHash(Math.random().toString()),
          info: {
            browser: 'Unknown',
            browserVersion: 'unknown',
            os: 'Unknown',
            osVersion: 'unknown',
            screenResolution: 'unknown',
            timezone: 'unknown',
            language: 'unknown',
            platform: 'unknown',
          },
        });
      }
      setIsLoading(false);
    }, 100);
  }, []);

  useEffect(() => {
    generate();
  }, [generate]);

  return {
    fingerprint,
    isLoading,
    regenerate: generate,
  };
}

/**
 * Get fingerprint synchronously (for immediate use)
 */
export function getDeviceFingerprintSync(): DeviceFingerprint {
  const info = generateDeviceInfo();
  const hash = generateFingerprint(info);
  return { hash, info };
}

export default useDeviceFingerprint;
